import { Box, Divider, FormHelperText, Grid, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useRef, useState } from 'react';
import { AnnouncementPersonalizeModal } from './AnnouncementPersonalizeModal';
import { AnnouncementPersonalizationDto } from '@inosio/payments-sdk';
import { extractPlaceholderKeysFromTheText } from './utils/utils';
import { PersonalizePopupToolbar } from './PersonalizePopupToolbar';

export interface SubjectInputFieldProps {
  value: string;
  onChange: (newSubject: string) => void;
  setFieldValue: (field: string, value: any) => void;
  personalizations: AnnouncementPersonalizationDto[];
  onPersonalizedFieldsChange: (subject: string, personalizations: AnnouncementPersonalizationDto[]) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  isError?: boolean;
  error?: string;
}

export const SubjectInputField = (props: SubjectInputFieldProps) => {
  const [openAnnouncementPersonalizeModal, setOpenAnnouncementPersonalizeModal] = useState<boolean>(false);
  const classes = useStyle();
  const maxCharLength = 120;
  const textFieldRef = useRef<HTMLInputElement | null>(null);
  const [cursorStart, setCursorStart] = useState<number | null>(null);
  const [cursorEnd, setCursorEnd] = useState<number | null>(null);
  const [selectedPersonalizedField, setSelectedPersonalizedField] = useState<AnnouncementPersonalizationDto>();
  const [toolbarPosition, setToolbarPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [mousePosition, setMousePosition] = useState<{ startX: number; endX: number }>({ startX: 0, endX: 0 });

  const [showToolbar, setShowToolbar] = useState<boolean>(false);

  const handlePersonalizeField = (selectedPersonalization: AnnouncementPersonalizationDto) => {
    const currentCursorStart = cursorStart ?? 0;
    const currentCursorEnd = cursorEnd ?? 0;

    const newSubject = props.value.slice(0, currentCursorStart) + selectedPersonalization.Key + props.value.slice(currentCursorEnd);
    props.setFieldValue('Subject', newSubject);
    props.onChange(newSubject);
    setOpenAnnouncementPersonalizeModal(false);
    const updatedPersonalizations = props.personalizations.some((f) => f.Key === selectedPersonalization.Key)
      ? props.personalizations.map((f) => (f.Key === selectedPersonalization.Key ? selectedPersonalization : f))
      : [...props.personalizations, selectedPersonalization];
    props.onPersonalizedFieldsChange(newSubject, updatedPersonalizations);
  };

  const handleSubjectChange = (newSubject: string) => {
    props.onChange(newSubject);
    props.setFieldValue('Subject', newSubject);
  };

  const handleOpenPersonalizedModal = () => {
    setShowToolbar(false);
    setOpenAnnouncementPersonalizeModal(true);
  };

  const handleCursorAndSelection = (event: React.SyntheticEvent<HTMLInputElement | HTMLDivElement | HTMLTextAreaElement>) => {
    resetToolbar();
    const inputElement = event.target as HTMLInputElement;
    const cursorStart = inputElement.selectionStart ?? 0;
    const cursorEnd = inputElement.selectionEnd ?? 0;

    if (cursorStart !== cursorEnd) {
      setCursorStart(cursorStart);
      setCursorEnd(cursorEnd);
      const selectedText = inputElement.value.slice(cursorStart, cursorEnd);
      const keys = extractPlaceholderKeysFromTheText(selectedText);
      if (keys.length) {
        const selectedFieldKey = keys[0];
        handleToolBarOnSelectedText(inputElement, true, selectedFieldKey);
      } else {
        resetToolbar();
      }
    } else {
      const textBeforeCursor = inputElement.value.slice(0, cursorStart);
      const match = textBeforeCursor.match(/{{([a-z_]+)}}$/);
      if (match) {
        const selectedFieldKey = match[0];
        setCursorStart(cursorStart - selectedFieldKey.length);
        setCursorEnd(cursorStart);
        handleToolBarOnSelectedText(inputElement, false, selectedFieldKey);
      } else {
        resetToolbar();
        setCursorStart(cursorStart);
        setCursorEnd(cursorEnd);
      }
    }
  };

  const handleToolBarOnSelectedText = (inputElement: HTMLInputElement, isSelected: boolean, selectedFieldKey: string | undefined) => {
    if (selectedFieldKey) {
      setSelectedPersonalizedField(props.personalizations?.find((field) => field.Key === selectedFieldKey));
      setShowToolbar(true);
      // Get the bounding client rect of the selected text to calculate its position
      const inputRect = inputElement.getBoundingClientRect();

      const toolbarOffset = 100;
      const leftOffset = isSelected ? 260 : 320;
      const positionX = mousePosition.startX === mousePosition.endX ? mousePosition.startX : (mousePosition.startX + mousePosition.endX) / 2;

      setToolbarPosition({
        top: inputRect.top - toolbarOffset,
        left: positionX - leftOffset,
      });
    } else {
      resetToolbar();
    }
  };

  const resetToolbar = () => {
    setShowToolbar(false);
    setSelectedPersonalizedField(undefined);
    setToolbarPosition({
      top: 0,
      left: 0,
    });
  };

  const handleMouseUpOnInput = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const endX = event?.clientX + window.scrollX;
    setMousePosition({
      ...mousePosition,
      endX: endX,
    });
  };

  const handleMouseDownOnInput = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const startX = event.clientX + window.scrollX;
    setMousePosition({
      ...mousePosition,
      startX: startX,
    });
  };

  const onHandleAnnouncementModalClose = () => {
    setOpenAnnouncementPersonalizeModal(false);
    resetToolbar();
  };

  return (
    <Grid>
      <Box className={`${classes.box} ${props.isError ? classes.errorBox : ''}`}>
        <TextField
          ref={textFieldRef}
          value={props.value}
          placeholder="Enter the subject of your email announcement here"
          className={classes.inputStyle}
          inputProps={{ maxLength: maxCharLength }}
          onChange={(e) => {
            handleSubjectChange(e.target.value);
            handleCursorAndSelection(e); // Track the cursor position on change
          }}
          onBlur={props.onBlur}
          onFocus={handleCursorAndSelection} // Capture cursor position on focus
          onSelect={handleCursorAndSelection}
          onClick={handleCursorAndSelection}
          onMouseDown={handleMouseDownOnInput}
          onMouseUp={handleMouseUpOnInput}
          name="Subject"
        />
        <PersonalizePopupToolbar
          show={showToolbar}
          selectedPersonalizedField={selectedPersonalizedField}
          position={toolbarPosition}
          onEdit={() => {
            setShowToolbar(false);
            setOpenAnnouncementPersonalizeModal(true);
          }}
        />
        <Divider />
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }} px={2} py={1}>
          <Typography
            onClick={handleOpenPersonalizedModal}
            sx={{
              fontWeight: 'bold',
              cursor: 'pointer',
              fontSize: '15px',
            }}
          >
            Personalize
          </Typography>
          <Typography
            sx={{
              fontWeight: 'light',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            {props.value?.length ?? 0}/{maxCharLength} Chars
          </Typography>
        </Box>
      </Box>
      <AnnouncementPersonalizeModal
        title="Personalize Subject"
        open={openAnnouncementPersonalizeModal}
        onClose={onHandleAnnouncementModalClose}
        onSelectPersonalizeField={handlePersonalizeField}
        defaultPeronalizeField={selectedPersonalizedField}
      />
      {props.isError ? (
        <FormHelperText sx={{}} error>
          {' '}
          {props.error}{' '}
        </FormHelperText>
      ) : null}
    </Grid>
  );
};

export const useStyle = makeStyles(() => ({
  box: {
    border: '2px solid #dddddd',
    borderRadius: '24px !important',
  },
  errorBox: {
    borderColor: '#d32f2f',
  },
  inputStyle: {
    width: '100% !important',
    height: '49px !important',
    border: 'none !important',
    boxShadow: 'none',
    borderRadius: '20px !important',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
}));
